<template>
  <div class="audio-player">
    <div class="audio-player__timing">
      <span>{{ currentTime }}</span>
      <span>{{ duration }}</span>
    </div>
    <v-progress-linear
      v-model="percentage"
      height="5"
      style="margin-top: 15px; margin-bottom: 15px"
      @click.native="setPosition()"
      :disabled="!loaded"
    />
    <div class="audio-player__controls">
      <v-menu bottom offset-y>
        <template v-slot:activator="{ on }">
          <v-btn small fab v-on="on">
            <span class="audio-player__speed">{{ currentSpeed }}x</span>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="speed in speedOptions"
            :key="speed"
            @click="changeSpeed(speed)"
          >
            <v-list-item-title> {{ speed }}x </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn small fab @click="seek(false)">
        <v-icon>replay_30</v-icon>
      </v-btn>
      <v-btn
        fab
        color="main"
        @click.native="playing ? pause() : play()"
        :disabled="!loaded"
      >
        <v-icon color="mainInvert" v-if="!playing || paused">play_arrow</v-icon>
        <v-icon color="mainInvert" v-else>pause</v-icon>
      </v-btn>
      <v-btn small fab @click="seek(true)">
        <v-icon>forward_30</v-icon>
      </v-btn>
      <v-btn small fab @click="$emit('openFullscreen')">
        <v-icon>fullscreen_exit</v-icon>
      </v-btn>
    </div>
    <audio
      id="player"
      ref="player"
      @ended="ended"
      @canplay="canPlay"
      :src="src"
    ></audio>
    <div class="d-flex justify-center">
      <span
        v-if="showSkipButton"
        class="audio-player__skip animated fadeIn"
        @click.prevent="skip"
        >{{ skipText }}</span
      >
    </div>
  </div>
</template>

<script>
import throttle from 'lodash/throttle';
import { format, addSeconds } from 'date-fns';

export default {
  name: 'AudioPlayerPro',
  props: {
    src: {},
    active: {},
    taking: Boolean,
    testing: Boolean,
    autoPlay: Boolean,
    downloadable: Boolean,
    ended: {
      type: Function,
      default: () => {},
    },
    canPlay: {
      type: Function,
      default: () => {},
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    active(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.stop();
      }
      if (!oldVal && newVal) {
        this.checkAutoplay();
      }
    },
    playing(value) {
      this.$emit('playing', value);
    },
  },
  data() {
    return {
      firstPlay: true,
      isMuted: false,
      loaded: false,
      playing: false,
      paused: false,
      skipped: false,
      maxPlayTime: 0,
      percentage: 0,
      currentTime: '00:00:00',
      audio: undefined,
      totalDuration: 0,
      currentSpeed: 1,
      speedOptions: [1, 1.5, 2],
      startTimeSet: false,
    };
  },
  computed: {
    duration() {
      return this.audio ? this.formatTime(this.totalDuration) : '';
    },
    savedPlayTime() {
      return this.options?.state?.current_time || 0;
    },
    showSkipButton() {
      return this.taking && this.testing && this.audioRequired;
    },
    skipText() {
      return this.skipped ? 'Skipped for Test' : 'Skip Requirement in Test';
    },
    audioRequired() {
      return this.options?.required || false;
    },
  },
  methods: {
    checkAutoplay() {
      if (this.taking && this.active && this.autoPlay) {
        this.play();
      } else {
        this.stop();
      }
    },
    setPosition() {
      let newPosition = parseInt((this.audio.duration / 100) * this.percentage);
      if (this.audioRequired && newPosition > this.maxPlayTime) {
        newPosition = this.maxPlayTime;
      }
      this.audio.currentTime = newPosition;
    },
    seek(forward) {
      let duration = this.audio.duration;
      let currentTime = parseInt((this.audio.duration / 100) * this.percentage);
      let newPosition = currentTime + (forward ? 30 : -30);

      if (forward) {
        newPosition = newPosition > duration ? duration : newPosition;
        if (this.audioRequired && newPosition > this.maxPlayTime) {
          newPosition = this.maxPlayTime;
        }
      } else {
        newPosition = newPosition > 0 ? newPosition : 0;
      }

      this.audio.currentTime = newPosition;

      setTimeout(() => {
        if (currentTime === 0 && newPosition === 30) {
          this.audio.play().then(() => {
            this.audio.currentTime = newPosition;
          });
        }
      });
    },
    stop() {
      this.audio.pause();
      this.paused = true;
      this.playing = false;
      this.audio.currentTime = 0;
    },
    play() {
      this.audio.play().then((_) => {
        this.playing = true;
        this.checkPlayerStartTime();
      });
      this.paused = false;
    },
    checkPlayerStartTime() {
      if (this.savedPlayTime && !this.startTimeSet) {
        this.audio.currentTime = this.savedPlayTime;
        this.startTimeSet = true;
      }
    },
    updateMaxPlayTime(currentTime) {
      if (currentTime > this.maxPlayTime) {
        this.maxPlayTime = currentTime;
      }
    },
    changeSpeed(val) {
      this.currentSpeed = val;
      this.audio.playbackRate = val;
    },
    pause() {
      this.paused = !this.paused;
      if (this.paused) {
        this.audio.pause();
        this.playing = false;
      } else {
        this.audio.play();
      }
    },
    download() {
      this.audio.pause();
      window.open(this.file, 'download');
    },
    mute() {
      this.isMuted = !this.isMuted;
      this.audio.muted = this.isMuted;
      this.volumeValue = this.isMuted ? 0 : 75;
    },
    reload() {
      this.audio.load();
    },
    skip() {
      this.stop();
      this.skipped = true;
      this._handleEnded();
    },
    _handleLoaded: function () {
      if (this.audio.readyState >= 2) {
        if (this.audio.duration === Infinity) {
          // Fix duration for streamed audio source or blob based
          // https://stackoverflow.com/questions/38443084/how-can-i-add-predefined-length-to-audio-recorded-from-mediarecorder-in-chrome/39971175#39971175
          this.audio.currentTime = 1e101;
          this.audio.ontimeupdate = () => {
            this.audio.ontimeupdate = () => {};
            this.audio.currentTime = 0;
            this.totalDuration = parseInt(this.audio.duration);
            this.loaded = true;
          };
        } else {
          this.totalDuration = parseInt(this.audio.duration);
          this.loaded = true;
        }
      } else {
        throw new Error('Failed to load sound file');
      }
    },
    _handlePlayingUI: function (e) {
      let time = this.audio.currentTime;
      this.percentage = (time / this.audio.duration) * 100;
      this.currentTime = this.formatTime(time);
      this.triggerCustomProgress(time);
      this.updateMaxPlayTime(time);
      this.playing = true;
    },
    _handlePlayPause: function (e) {
      if (e.type === 'play' && this.firstPlay) {
        // in some situations, audio.currentTime is the end one on chrome
        this.audio.currentTime = 0;
        if (this.firstPlay) {
          this.firstPlay = false;
        }
      }
      if (
        e.type === 'pause' &&
        this.paused === false &&
        this.playing === false
      ) {
        this.currentTime = '00:00:00';
      }
    },
    _handleEnded() {
      this.$emit('ended');
      this.paused = this.playing = false;
    },
    init: function () {
      // on iOS loadeddata event not firing without autoplay param for some reason
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        this.audio.autoplay = true;
      }

      this.togglePlayerEventListeners(true);
    },
    triggerCustomProgress: throttle(function (currentTime) {
      // for required videos save only max play time
      if (
        !this.audioRequired ||
        (this.audioRequired && currentTime > this.savedPlayTime)
      ) {
        this.$emit('customProgress', {
          current_time: currentTime,
          url: this.src,
        });
      }
    }, 1000 * 15),

    formatTime(seconds) {
      const date = new Date(0).setHours(0, 0, seconds, 0);
      return format(date, 'HH:mm:ss');
    },

    togglePlayerEventListeners(add = true) {
      const method = add ? 'addEventListener' : 'removeEventListener';
      const playerEvents = {
        timeupdate: '_handlePlayingUI',
        loadeddata: '_handleLoaded',
        pause: '_handlePlayPause',
        play: '_handlePlayPause',
        ended: '_handleEnded',
      };

      for (let key in playerEvents) {
        this.audio[method](key, this[playerEvents[key]]);
      }
    },
  },
  mounted() {
    this.audio = this.$refs.player;
    this.init();
    this.checkAutoplay();
  },
  beforeDestroy() {
    this.togglePlayerEventListeners(false);
  },
};
</script>

<style lang="less">
.audio-player {
  margin-top: 16px;

  .v-progress-linear {
    cursor: pointer;
  }

  &__timing {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
  }

  &__controls {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px 0;

    .v-btn {
      margin: 0 8px !important;
    }
  }

  &__speed {
    font-size: 12px;
    font-weight: 900;
  }

  &__skip {
    color: var(--v-primary-base);
    font-weight: bold;
    cursor: pointer;
  }
}
</style>
